/**
 * What the person behind the counter needs to see.
 *
 * Three questions, and none of them had a screen before: what has been sold in
 * the last while, how many receipts are waiting to be filed, and whether this
 * kiosk is holding money the server has not accepted. The last one is the same
 * banner the boot screen shows, repeated here because this is where somebody
 * looks when a customer says their receipt never came.
 *
 * **A row opens to show what was ordered**, and that is not a convenience.
 * Printing is designed to degrade — no printer, a queue that refused, a canvas
 * that would not allocate — and the sale completes anyway, which is right,
 * because a shop that stops trading over a paper jam is worse. But the slip was
 * the *only* copy of what the customer asked for: this list carried a number, a
 * time and a total, and nothing in the shop could say which drinks a paid order
 * was for. A jam during a rush meant orders nobody could make. `orders.get`
 * already returns every line with its add-ons, so this needed no new API — only
 * a screen willing to ask.
 */
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { format, fromMinor } from '@mgc/domain'
import type { Money } from '@mgc/domain'
import { Button, Card, Notice, Spinner } from '@/components/ui'
import { refusalOf, trpc } from '@/api/client'
import { useRefusal, useT } from '@/i18n'
import { confirmTaken, needsAPerson, writeOff } from '@/outbox/outbox'
import { useSession } from '@/store/session'

export function Attendant({ onClose }: { onClose: () => void }) {
  const t = useT()
  const refusalText = useRefusal()
  const [problem, setProblem] = useState<string | null>(null)
  /** The row an attendant has opened, if any. One at a time. */
  const [opened, setOpened] = useState<string | null>(null)
  const outbox = useSession((s) => s.outbox)
  const refreshOutbox = useSession((s) => s.refreshOutbox)
  const staffName = useSession((s) => s.staffName)
  const stuck = outbox.filter(needsAPerson)

  /**
   * Say what happened to a claim nobody could resolve automatically.
   *
   * Both answers are recorded with the name of whoever gave them: `confirm`
   * turns the claim into an ordinary one that replays, and `writeOff` settles
   * it without deleting anything. Neither is a delete, because
   * `docs/offline.md` is explicit that a bounded outbox is a data-loss policy
   * wearing a retention policy's clothes.
   */
  const resolve = async (id: string, taken: boolean) => {
    const by = staffName ?? 'attendant'
    const reason = taken ? 'confirmed against the terminal batch' : 'the terminal batch showed no charge'

    // The server keeps the record; IndexedDB keeps the kiosk honest if the
    // server is unreachable. Both, in that order, because the outbox is the
    // thing that must not lose it.
    try {
      await trpc.orders.resolveClaim.mutate({
        paymentId: id,
        outcome: taken ? 'taken' : 'not-taken',
        reason
      })
    } catch {
      // Offline. The local decision still stands and replays with the rest.
    }

    if (taken) await confirmTaken(id, by)
    else await writeOff(id, by, reason)
    await refreshOutbox()
  }

  /**
   * Void a basket nobody paid for.
   *
   * The order rows on this screen were inert `<li>`s, and `order.status` has
   * accepted `'void'` since it was written with nothing to set it — so an
   * abandoned basket sat here looking like a sale about to happen, holding a 1C
   * reservation.
   */
  const voidOrder = useMutation({
    mutationFn: (orderId: string) =>
      trpc.orders.void.mutate({ orderId, reason: `voided by ${staffName ?? 'an attendant'}` }),
    onSuccess: () => void recent.refetch(),
    onError: (error) => setProblem(refusalText(refusalOf(error)))
  })

  const recent = useQuery({
    queryKey: ['recent'],
    queryFn: () => trpc.orders.recent.query({ limit: 25 }),
    refetchInterval: 20_000
  })

  const backlog = useQuery({
    queryKey: ['fiscal-backlog'],
    queryFn: () => trpc.fiscal.backlog.query(),
    refetchInterval: 30_000
  })

  return (
    <div className="mx-auto flex h-full w-full max-w-3xl flex-col gap-5 overflow-y-auto p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-[length:var(--text-kiosk-xl)] font-bold">{t('attendant.title')}</h1>
        <Button variant="ghost" onClick={onClose}>
          {t('settings.close')}
        </Button>
      </header>

      {problem !== null && <Notice tone="bad">{problem}</Notice>}

      {outbox.length > 0 && (
        <Notice tone="bad">
          {t('recovery.body')} ({outbox.length})
        </Notice>
      )}

      {/*
        The claims that cannot resolve themselves, and the two buttons that
        resolve them. This section is the whole reason the attendant screen is
        not read-only: an `unresolved` claim is money the terminal would not
        confirm, and only a person holding the terminal's own batch report can
        say which way it went.
      */}
      {stuck.length > 0 && (
        <Card className="space-y-3 p-5">
          <h2 className="font-bold">{t('recovery.unresolved')}</h2>
          <p className="text-[var(--color-ink-soft)]">{t('recovery.unresolvedBody')}</p>
          <ul className="space-y-3">
            {stuck.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center justify-between gap-3">
                <span className="tabular-nums">
                  {format(fromMinor(BigInt(entry.amount)))}
                  <span className="ml-2 text-[var(--color-ink-soft)]">{entry.reference ?? entry.id}</span>
                </span>
                <span className="flex gap-2">
                  <Button onClick={() => void resolve(entry.id, true)}>{t('recovery.confirmTaken')}</Button>
                  <Button variant="ghost" onClick={() => void resolve(entry.id, false)}>
                    {t('recovery.writeOff')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="space-y-2 p-5">
        <h2 className="font-bold">{t('attendant.fiscal')}</h2>
        {backlog.isLoading ? (
          <Spinner />
        ) : (
          <>
            {/*
              Queued is normal and drains on its own; failed has stopped
              retrying and is a support ticket, so the two are not one number.
            */}
            <Row label={t('attendant.fiscalQueued')} value={String(backlog.data?.queued ?? 0)} />
            <Row
              label={t('attendant.fiscalFailed')}
              value={String(backlog.data?.failed ?? 0)}
              tone={(backlog.data?.failed ?? 0) > 0 ? 'bad' : undefined}
            />
          </>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="mb-3 font-bold">{t('attendant.recent')}</h2>
        {recent.isLoading ? (
          <Spinner />
        ) : (recent.data ?? []).length === 0 ? (
          <p className="text-[var(--color-ink-soft)]">{t('attendant.noOrders')}</p>
        ) : (
          <ul className="divide-y divide-[var(--color-line)]">
            {(recent.data ?? []).map((order) => (
              <li key={order.id} className="py-2">
                <div className="flex items-baseline justify-between gap-4">
                  {/*
                    The number, the time and the total are the button. An
                    attendant looking for "the flat white that never printed"
                    has the order number off the customer's screen or nothing at
                    all, so the row they can already identify is the row that
                    has to open.
                  */}
                  <button
                    type="button"
                    onClick={() => setOpened((current) => (current === order.id ? null : order.id))}
                    aria-expanded={opened === order.id}
                    /*
                      `self-stretch`, so the target is the whole height of the
                      row rather than the 34px the baseline-aligned text
                      occupied. The row is already 72px tall — the void button
                      beside it sets that — and an attendant reaching for an
                      order under a queue of customers was pressing a band a
                      third of what it looked like.
                    */
                    className="flex flex-1 items-center gap-4 self-stretch text-left"
                  >
                    <span className="font-semibold tabular-nums">{String(order.sequence).padStart(3, '0')}</span>
                    <span className="flex-1 text-[var(--color-ink-soft)]">
                      {new Date(order.placedAt).toLocaleTimeString()}
                    </span>
                    <span className={order.status === 'paid' ? '' : 'text-[var(--color-warn)]'}>{order.status}</span>
                    <span className="font-bold tabular-nums">{format(order.gross as Money)}</span>
                  </button>
                  {/*
                    Only an unpaid basket. A void is not a refund, and refunds are the
                    ERP's — see D24. Money that moved and cannot be accounted for is a
                    claim to resolve, not a status to change.
                  */}
                  {order.status === 'open' && (
                    <Button variant="ghost" onClick={() => voidOrder.mutate(order.id)} disabled={voidOrder.isPending}>
                      {t('attendant.void')}
                    </Button>
                  )}
                </div>

                {opened === order.id && <OrderLines orderId={order.id} />}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

/**
 * What one order was actually for.
 *
 * Fetched only when a row is opened. Loading lines for twenty-five orders to
 * show one is a query per row against a table that grows for the life of the
 * shop, and the attendant wants exactly one of them — the order a customer is
 * standing at the counter asking about.
 */
function OrderLines({ orderId }: { orderId: string }) {
  const t = useT()
  const order = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => trpc.orders.get.query({ id: orderId })
  })

  if (order.isLoading) return <Spinner />
  if (order.isError) return <Notice tone="warn">{t('attendant.linesFailed')}</Notice>

  const lines = order.data?.lines ?? []
  if (lines.length === 0) return <p className="text-[var(--color-ink-soft)]">{t('attendant.linesEmpty')}</p>

  return (
    <ul className="mt-2 space-y-2 rounded-[var(--radius-card)] bg-[var(--color-paper-sunk)] p-4">
      {lines.map((line) => (
        <li key={line.id}>
          <p className="flex items-baseline justify-between gap-3">
            <span className="font-semibold tabular-nums">{line.quantity}×</span>
            <span className="flex-1">
              {line.name}
              {line.temperature !== null && ` · ${line.temperature === 'ice' ? t('product.ice') : t('product.hot')}`}
              {line.changeLabel !== null && ` · ${line.changeLabel}`}
            </span>
            <span className="font-bold tabular-nums">{format(line.total as Money)}</span>
          </p>
          {/*
            Add-ons indented under their line rather than flattened into it: the
            person making the drink reads "latte, extra shot", and a list that
            puts the shot beside the latte reads as two drinks.
          */}
          {line.addOns.length > 0 && (
            <p className="pl-8 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-soft)]">
              {line.addOns.map((addOn) => `${addOn.quantity}× ${addOn.label}`).join(' · ')}
            </p>
          )}
          {line.note !== null && line.note !== '' && (
            <p className="pl-8 text-[length:var(--text-kiosk-sm)] italic">{line.note}</p>
          )}
        </li>
      ))}
    </ul>
  )
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <p className="flex justify-between border-b border-[var(--color-line)] py-2 last:border-0">
      <span className="text-[var(--color-ink-soft)]">{label}</span>
      <span className={`font-medium tabular-nums ${tone === 'bad' ? 'text-[var(--color-bad)]' : ''}`}>{value}</span>
    </p>
  )
}
